import { useStaticVirtualizerMeasure, Virtualizer } from '@fluentui-contrib/react-virtualizer';
import {
    ComboboxOpenChangeData,
    ComboboxOpenEvents,
    ComboboxProps,
    makeStyles,
    OptionOnSelectData,
    SelectionEvents,
    useMergedRefs,
    useTimeout,
} from '@fluentui/react-components';
import { MutableRefObject, RefCallback, useCallback, useRef } from 'react';

/** Shared constant for listbox max height in pixels */
export const LISTBOX_MAX_HEIGHT = 350;

/** Threshold for enabling virtualization - only virtualize if we have more items than this */
export const VIRTUALIZATION_THRESHOLD = 50;

/** Default height of each virtualized item in pixels */
export const DEFAULT_ITEM_SIZE = 32;

const useStyles = makeStyles({
    listbox: {
        // !important is intentional. Fluent docs say to use the prop {positioning: {autoSize: true}} to auto-size the Virtualized Combobox, but it doesn't work as intended.
        maxHeight: `${LISTBOX_MAX_HEIGHT}px !important`,
    },
});

/** Config options for useVirtualizedCombobox hook */
export interface UseVirtualizedComboboxOptions<T = any> {
    /** Height of each item in pixels */
    itemSize?: number;
    /** The currently selected option value (e.g., the code) - optional for scroll positioning */
    selectedOptions?: string[];
    /** The current list of matching/filtered options - optional for scroll positioning */
    matchingOptionsData?: T[];
    /** Function to extract the option key from an item - optional for scroll positioning */
    optionToSelectedOption?: (option: T) => string;
    /** Original onOptionSelect handler - optional for scroll positioning */
    onOptionSelect?: (event: SelectionEvents, data: OptionOnSelectData) => void;
}

/** Props returned by useVirtualizedCombobox hook */
export interface VirtualizedComboboxConfig {
    /** Ref callback to attach to Combobox listbox prop */
    scrollRef: RefCallback<HTMLDivElement>;
    /** Class name for listbox styling */
    listboxClassName: string;
    /** Length for virtualizer */
    virtualizerLength: number;
    /** Buffer items count */
    bufferItems: number;
    /** Buffer size */
    bufferSize: number;
    /** Container size ref for virtualizer */
    containerSizeRef: MutableRefObject<number>;
    /** Enhanced onOptionSelect that tracks selection index */
    onOptionSelect?: (event: SelectionEvents, data: OptionOnSelectData) => void;
    /** onOpenChange handler that scrolls to selected item */
    onOpenChange?: ComboboxProps['onOpenChange'];
}

/**
 * Hook to set up virtualization for a Combobox.
 * Returns config that should be spread to both Combobox.listbox and VirtualizedOptionList.
 */
export function useVirtualizedCombobox<T = any>(options: UseVirtualizedComboboxOptions<T> = {}): VirtualizedComboboxConfig {
    const {
        itemSize = DEFAULT_ITEM_SIZE,
        selectedOptions,
        matchingOptionsData,
        optionToSelectedOption,
        onOptionSelect: originalOnOptionSelect,
    } = options;

    const styles = useStyles();
    const containerSizeRef = useRef<number>(LISTBOX_MAX_HEIGHT);
    const selectedIndexRef = useRef<number>(0);
    const [setScrollTimer, clearScrollTimer] = useTimeout();

    const { virtualizerLength, bufferItems, bufferSize, scrollRef } = useStaticVirtualizerMeasure({
        defaultItemSize: itemSize,
        direction: 'vertical',
    });
    const mergedRefs = useMergedRefs(scrollRef);
    // Only set up scroll positioning if required config is provided
    const enableScrollPositioning = !!(selectedOptions && matchingOptionsData && optionToSelectedOption && originalOnOptionSelect);

    /**
     * Enhanced onOptionSelect that tracks selection index
     */
    const onOptionSelect = useCallback(
        (event: SelectionEvents, data: OptionOnSelectData) => {
            if (!enableScrollPositioning || !originalOnOptionSelect) return;

            if (data.optionValue && matchingOptionsData && optionToSelectedOption) {
                const index = matchingOptionsData.findIndex((item) => optionToSelectedOption(item) === data.optionValue);
                if (index !== -1) {
                    selectedIndexRef.current = index;
                }
            }
            originalOnOptionSelect(event, data);
        },
        [enableScrollPositioning, matchingOptionsData, optionToSelectedOption, originalOnOptionSelect]
    );

    /**
     * onOpenChange handler that scrolls to selected item
     */
    const onOpenChange: ComboboxProps['onOpenChange'] = useCallback(
        (_e: ComboboxOpenEvents, data: ComboboxOpenChangeData) => {
            if (!enableScrollPositioning) return;

            clearScrollTimer();
            if (data.open && selectedOptions && matchingOptionsData && optionToSelectedOption) {
                // Calculate current selected index when dropdown opens
                const currentSelectedValue = selectedOptions[0];
                if (currentSelectedValue) {
                    const index = matchingOptionsData.findIndex((item) => optionToSelectedOption(item) === currentSelectedValue);
                    if (index !== -1) {
                        selectedIndexRef.current = index;
                    }
                }

                // Scroll to position after dropdown renders
                setScrollTimer(() => {
                    const scrollPosition = itemSize * selectedIndexRef.current;
                    const scrollElement = mergedRefs.current;
                    if (scrollElement?.scrollTo && typeof scrollElement.scrollTo === 'function') {
                        scrollElement.scrollTo({
                            top: scrollPosition,
                            behavior: 'auto',
                        });
                    }
                }, 1);
            }
        },
        [
            enableScrollPositioning,
            selectedOptions,
            matchingOptionsData,
            optionToSelectedOption,
            scrollRef,
            itemSize,
            setScrollTimer,
            clearScrollTimer,
        ]
    );

    return {
        scrollRef: mergedRefs,
        listboxClassName: styles.listbox,
        virtualizerLength,
        bufferItems,
        bufferSize,
        containerSizeRef,
        ...(enableScrollPositioning && {
            onOptionSelect,
            onOpenChange,
        }),
    };
}

export interface VirtualizedOptionListProps<T> {
    items: T[];
    renderOption: (item: T, index: number) => React.ReactNode;
    itemSize?: number;
    /** Virtualization config from useVirtualizedCombobox hook */
    virtualizerConfig: VirtualizedComboboxConfig;
}

export function VirtualizedOptionList<T>({
    items,
    renderOption,
    itemSize = DEFAULT_ITEM_SIZE,
    virtualizerConfig,
}: VirtualizedOptionListProps<T>) {
    const { virtualizerLength, bufferItems, bufferSize, containerSizeRef } = virtualizerConfig;

    // Only virtualize if we have many items
    if (items.length <= VIRTUALIZATION_THRESHOLD) {
        return <>{items.map((item, index) => renderOption(item, index))}</>;
    }

    return (
        <Virtualizer
            key={`virtualizer-${items.length}`}
            numItems={items.length}
            virtualizerLength={virtualizerLength}
            bufferItems={bufferItems}
            bufferSize={bufferSize}
            itemSize={itemSize}
            containerSizeRef={containerSizeRef}
        >
            {(index: number) => renderOption(items[index], index)}
        </Virtualizer>
    );
}
